// @flow

import $ from 'jquery'

import Util from '../util/Util'
import Dropdown from '../components/Dropdown'
import Translation from '../i18n/Translation'
import OrderSuggestions from './OrderSuggestions'

type ValidationResponse = {
  success: boolean,
  message?: string,
}

export default class OrderValidation {

  static _getRequiredMessage (element: Object) {
    const type = element.attr('type')

    switch (type) {
      case 'text': return Translation.getText('empty_input')
      case 'hidden':
        if (element.hasClass('hidden-dropdown')) {
          return Translation.getText('empty_dropdown')
        }
        break
      case 'radio': return Translation.getText('empty_radio')
      case 'checkbox': return Translation.getText('empty_checkbox')
      default:
    }

    return Translation.getText('empty_input')
  }

  mode = 'orders'

  static setMode (mode: string) {
    OrderValidation.mode = mode
  }
  static getPrefix() {
    return "vol_" + OrderValidation.mode
  }

  static _isValidPostalCode (postalCode: string): boolean {
    return /^(?!01000|99999)(0[1-9]\d{3}|[1-9]\d{4})$/g.test(postalCode)
  }

  static isValidAngel (angel: string): boolean {
    let q = parseInt(angel)
    if (q >= 0 && q <= 90) {
      return true
    }
    return false
  }

  static async isValidPostalCode (postalCode: string): Promise<boolean> {
    const isValid = OrderValidation._isValidPostalCode(postalCode) &&
      await OrderSuggestions.getsCitiesByPostalCode(postalCode)

    return isValid
  }

  static async isValidCity (postalCode: ?string, city: string): Promise<boolean> {
    const { success, cities } = await OrderSuggestions._getSuggestions('cities', { postal_code: postalCode })

    return success && cities && cities.map(city => city.toLowerCase()).includes(city.toLowerCase())
  }

  static async isValidStreet (postalCode: ?string, city: ?string, street: string): Promise<boolean> {
    const { success, streets } = await OrderSuggestions._getSuggestions('streets', { postal_code: postalCode, city })

    //TODO: Temp FIXIT
    return true;
    //return success && streets && streets.map(street => street.toLowerCase()).includes(street.toLowerCase())
  }

  // Marktlokations-ID: 51050982574
  static isValidMarketIdentifier (value: string) {

    if (value.length != 11) return false

    const excludes = [0,
      11111111111,
      22222222222,
      33333333333,
      44444444444,
      55555555555,
      66666666666,
      77777777777,
      88888888888,
      99999999999
    ]
    let match = excludes.includes(parseInt(value))
    if (match) return false

    if (value.match(/^\d+$/)) {
      // validation
      // step a)
      let odd = 0
      let count = 0

      for (count = 0; count < 10; count++) {
        odd += parseInt(value.substr(count, 1))
        count++
      }

      // step b)
      let even = 0

      for (count = 1; count < 10; count++) {
        even += parseInt(value.substr(count, 1))
        count++
      }

      even *= 2

      // step c)
      const sum = even + odd // == subtrahent

      // step d)
      const minuend = 10 * Math.ceil(sum / 10)
      const expected = minuend - sum

      // return true if correct / false if not correct
      return parseInt(value.substr(10, 1)) === expected
    }

    return false
  }

  static async isTypeValid (key: string, value: string): Promise<ValidationResponse> {

    const element = $(`[name="${key}"]`)
    const type = element.attr('type')

    if (type === 'radio' && value == 'null') {
      return { success: false, message: Translation.getText('empty_checkbox') }
    }

    if (type === 'checkbox' && value !== 'true') {
      return { success: false, message: Translation.getText('empty_checkbox') }
    }

    if (key.includes('iban')) {
      if (!/DE[a-zA-Z0-9]{2}\s?([0-9]{4}\s?){4}([0-9]{2})\s?/g.test(value)) {
        if (!value) {
          OrderValidation._setBankInformation('', '')
        }

        return ({ success: false, message: Translation.getText('iban') })
      }
      else {
        await OrderValidation.loadBankInformation(value)
      }
    }

    let pf = OrderValidation.getPrefix()

    if (key.includes('peak_load')) {
      const currentIndex = Util._getCurrentIndex(key)
      const currentCounterType = localStorage.getItem(`${pf}_consumptionpoint_consumption_type[${currentIndex}]`)
      const amount = localStorage.getItem(`${pf}_consumptionpoint_consumption_amount[${currentIndex}]`)
      const peak = Util.getNumberFromSeparatedNumberString(value)

      if (currentCounterType === 'RLM') {

        let valid = true;
        if (peak < 1 || peak > 8760) valid = false;

        let ratioValid = (amount / peak) <= 8760;

        if (ratioValid === false) valid = false;

        if (valid == false) {
          return ({
            success: false,
            message: 'Ungültige Lastspitze'
          })
        }
      }
    }

    if (key.includes('consumption_amount')) {
      const currentIndex = Util._getCurrentIndex(key)
      const currentCounterType = localStorage.getItem(`${pf}_consumptionpoint_consumption_type[${currentIndex}]`)
      const kWh = Util.getNumberFromSeparatedNumberString(value)
      const isPrivate = localStorage.getItem(`${pf}_customer_type`) === 'private'

      const privateMax = 99999
      const rlmMin = 3000 // 35000
      const rlmMax = isPrivate ? privateMax : 10000000
      const slpMin = 1
      const slpMax = isPrivate ? privateMax : 99999

      if (currentCounterType === 'RLM' && kWh < rlmMin) {
        return ({
          success: false,
          message: Translation.getText(
            'consumption_amount_min_rlm',
            { min: Util.getSeparatedNumberString(String(rlmMin)) }
          ),
        })
      }

        if (kWh < rlmMin) {
            return ({
                success: false,
                message: "Eingabe: ≥ 3.000 kWh"
            })
        }

      if (currentCounterType === 'RLM' && kWh > rlmMax) {
        return ({
          success: false,
          message: Translation.getText(
            'consumption_amount',
            { min: Util.getSeparatedNumberString(String(rlmMin)), max: Util.getSeparatedNumberString(String(rlmMax)) }
          ),
        })
      }

      if (currentCounterType === 'SLP' && kWh < slpMin) {
        return ({
          success: false,
          message: Translation.getText(
            'consumption_amount_min',
            { min: Util.getSeparatedNumberString(String(slpMin)) }
          ),
        })
      }

      if (currentCounterType === 'SLP' && kWh > slpMax) {
        return ({
          success: false,
          message: Translation.getText(
            'consumption_amount',
            { min: Util.getSeparatedNumberString(String(slpMin)), max: Util.getSeparatedNumberString(String(slpMax)) }
          ),
        })
      }
    }

    if (key.includes('email')) {
      // eslint-disable-next-line no-control-regex, max-len
      const emailRegex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g

      if (!emailRegex.test(value)) {
        return ({ success: false, message: Translation.getText('email') })
      }
    }

    if (key.includes('phone')) {
      // eslint-disable-next-line no-unused-vars
      const phoneRegex = /\(?\(?(0|49)\)?[ ()]?([- ()]?\d[- ()]?){5,}/g

      if (!/[0-9]{5,}/g.test(value)) {
        return ({ success: false, message: Translation.getText('phone') })
      }
    }

    if (key.includes('postal_code') && !await OrderValidation.isValidPostalCode(value)) {
      return ({ success: false, message: Translation.getText('postal_code') })
    }

    if (key.includes('connectiondata_angle') && !OrderValidation.isValidAngel(value)) {
      return ({ success: false, message: 'Nur Werte von 0-90 Grad sind zulässig.' })
    }

    const postalCode = (document.querySelector(`input[name="${key.replace('city', 'postal_code')}"]`) || {}).value || ''

    if (key.includes('city') && !await OrderValidation.isValidCity(postalCode, value)) {
      return ({ success: false, message: Translation.getText('city') })
    }

    if (
      key.includes('street') &&
      !await OrderValidation.isValidStreet(
        localStorage.getItem(key.replace('street', 'postal_code')),
        localStorage.getItem(key.replace('street', 'city')),
        value
      )
    ) {
      return ({ success: false, message: Translation.getText('street') })
    }

    if (key.includes('account_owner') && !value.includes(' ')) {
      return ({ success: false, message: Translation.getText('account_owner') })
    }

    if (/(first|last)name/.test(key) && /\d/.test(value)) {
      return ({ success: false, message: Translation.getText('name') })
    }

    if (key.includes('market_identifier') && !OrderValidation.isValidMarketIdentifier(value)) {
      return ({ success: false, message: Translation.getText('market_identifier') })
    }

    return { success: true }
  }

  static async _validateRequired (
    key: string,
    requiredParts: Array<string>,
    value: string,
    element: Object
  ): Promise<ValidationResponse> {
    const requiredPart = requiredParts.shift()
    const [ conditionalKey, conditionalValue ] = requiredPart.split('=')

    // is required if another field has a specific value and is empty
    if (conditionalKey && localStorage.getItem(conditionalKey) === conditionalValue && !value) {
      if (requiredParts instanceof Array && requiredParts.length > 0) {
        return OrderValidation._validateRequired(key, requiredParts, value, element)
      }

      return { success: false, message: OrderValidation._getRequiredMessage(element) }
    }

    // is required and not empty
    if ((conditionalKey && localStorage.getItem(conditionalKey) === conditionalValue) && value) {
      const isValid = await OrderValidation.isTypeValid(key, value)

      if (requiredParts instanceof Array && requiredParts.length > 0) {
        return OrderValidation._validateRequired(key, requiredParts, value, element)
      }

      return isValid
    }

    return { success: true }
  }

  static async validateInput (key: string, _value: string): Promise<ValidationResponse> {


    let element = $(`[name="${key}"]`)
    let value = String(_value).trim()

    if (element.length > 1 && element.last().attr('type') === 'checkbox') {
      element = element.last()
      value = element.prop('checked')
    }

    const required = element.attr('data-required') || ''

    //Ausnahme: Immer checken
     const currentIndex = Util._getCurrentIndex(key)
     const slp = localStorage.getItem(`vol_orders_consumptionpoint_consumption_type[${currentIndex}]`)

    if (slp == 'SLP' && key.includes("vol_orders_consumptionpoint_meter_installation")) {
       if (value == 'null') {
        return  { success: false, message: Translation.getText('empty_checkbox') }
      }
    }

    // is optional or valid
    if (!required) {
      return { success: true }
    }

    const isRequired = required === 'true'

    //console.log ("validate Input")
    //console.log ("req: " + isRequired)
    //console.log (element)
    //console.log (key)
    //console.log (_value)


    if (key.includes("vol_orders_connectiondata_market_identifier")) {

      if (value == "") return { success: true }

      const isValid = await OrderValidation.isTypeValid(key, value)
      return isValid
    }
    // is required and is empty
    else if (isRequired && !value) {
      return { success: false, message: OrderValidation._getRequiredMessage(element) }
    }
    else if (isRequired) {

      const isValid = await OrderValidation.isTypeValid(key, value)
      return isValid
    }

    const requiredParts = required.split('&&')
    const result = await OrderValidation._validateRequired(key, requiredParts, value, element)

    return result
  }

  static _setBankInformation (bic: string, name: string) {

    let pf = OrderValidation.getPrefix()

    localStorage.setItem(`${pf}_contract_bic`, bic)
    localStorage.setItem(`${pf}_contract_credit_institution`, name)

    $(`input[name="${pf}_contract_bic"]`).val(bic)
    $(`input[name="${pf}_contract_credit_institution"]`).val(name)
  }

  static async loadBankInformation (iban: string) {
    let pf = OrderValidation.getPrefix()

    let result = {}

    let url = '/bestellprozess?tx_voltego_orders%5Baction%5D=validateiban&tx_voltego_orders%5Bcontroller%5D=Ajax&type=1335898897'

    if (pf == 'dm') {
      url = '/direktvermarktung?tx_voltego_directmarketing%5Baction%5D=validateiban&tx_voltego_directmarketing%5Bcontroller%5D=Dmajax&type=1335898898'
    }

    try {
      result = await $.ajax({
        type: 'POST',
        // eslint-disable-next-line max-len
        url: url,
        contentType: 'application/x-www-form-urlencoded',
        data: `iban=${iban}`,
      })
    }
    catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)

      OrderValidation.setInputError(true, `${pf}_contract_iban', 'Die IBAN konnte nicht geprüft werden!`)

      return
    }

    const { status, bank_information: bankInformation } = result
    const isValid = status === 'valid'
    const bic = isValid ? bankInformation.bic : ''
    const name = isValid ? bankInformation.name : ''

    OrderValidation._setBankInformation(bic, name)

    if (isValid) {
      OrderValidation.setInputError(false, `${pf}_contract_bic`)
      OrderValidation.setInputError(false, `${pf}_contract_credit_institution`)
    }

    OrderValidation.setInputError(!isValid, `${pf}_contract_iban`, 'Die IBAN ist ungültig')
  }

  static setInputError (hasError: boolean, elementId: string, errorMessage?: string) {
    if (document.getElementById(`${elementId}_container`)) {
      if (hasError) {
        document.getElementById(`${elementId}_container`).classList.add('has-error')
        document.getElementById(`${elementId}_error_message`).classList.remove('hidden')
        document.getElementById(`${elementId}_error_message`).innerText = errorMessage
      }
      else {
        document.getElementById(`${elementId}_container`).classList.remove('has-error')
        let em = document.getElementById(`${elementId}_error_message`)
        if (em) {
          em.classList.add('hidden')
          em.innerText = ''
        }
      }
    }
  }

  static handleField (name: string) {
    let pf = OrderValidation.getPrefix()

    if (name && name.includes(`vol_dm_connectiondata_peak_power`)) {
      const index = Util._getCurrentIndex(name)
      let $d = $(`input[name="vol_dm_connectiondata_remote[${index}]"]`).filter(`input[type="checkbox"]`)

      if (parseInt(localStorage.getItem(`vol_dm_connectiondata_peak_power[${index}]`)) >= 100) {
        localStorage.setItem(`vol_dm_connectiondata_remote[${index}]`, true)
        $d[0].checked = true
        $d.attr("disabled", true)
      } else {
        $d.attr("disabled",false)
      }
    }

    /*
    if (name && name.includes(`vol_orders_consumptionpoint_meter_installation`)) {

      const index = Util._getCurrentIndex(name)

      let $d = $(`input[name="vol_dm_connectiondata_remote[${index}]"]`).filter(`input[type="radio"]`)

      const type = localStorage.getItem(`vol_orders_consumptionpoint_consumption_type[${index}]`)

      if (type == "SLP") {
        const validationName = `vol_orders_consumptionpoint_meter_installation[${index}]`
        let val = localStorage[validationName]
        if (val == "null" || val == undefined || val == null || val == "") {
          OrderValidation.setInputError(false, validationName, "nix")
        } else {
          OrderValidation.setInputError(true, validationName, "")
        }
      }
    }
    */

    if (localStorage.getItem(`vol_order_completed`) === 'true') {
      $('.standalone-only').addClass('vol_order hidden')
    }

    if (localStorage.getItem(`${pf}_customer_type`) === 'business') {
      $('.private-only').addClass('vol_order hidden')
      $('.business-only').removeClass('vol_order hidden')

      // Todo: Handle radio buttons
    }
    else {
      $('.private-only').removeClass('vol_order hidden')
      $('.business-only').addClass('vol_order hidden')

      Object.keys(localStorage).forEach(key => {
        if (key.includes(`${pf}_consumptionpoint_consumption_type`)) {
          OrderValidation.handleRLMOnly(key)
        }
      })
    }

    Object.keys(localStorage).forEach(key => {
      if (key.includes(`${pf}_connectiondata_change`) ||
          key.includes(`${pf}_connectiondata_old_contract_canceled`)
         ) {
            OrderValidation.handleSupChangeOnly(key)
          }
    })

    if (localStorage.getItem(`${pf}_contract_payment`) === 'direct_debit') {
      $('.sepa-only').removeClass('vol_order hidden')
    }
    else {
      $('.sepa-only').addClass('vol_order hidden')
    }

    if (name && name.includes('plant_type')) {

      const index = Util._getCurrentIndex(name)

      let $parentNode = $(`div[data-pointkey="${index}"]`)
      if (localStorage.getItem(`vol_dm_connectiondata_plant_type[${index}]`) === 'photovoltaics') {
        $parentNode.find('.photovoltaics-only').removeClass('vol_order hidden')
      } else {
        $parentNode.find('.photovoltaics-only').addClass('vol_order hidden')
      }
    }
    if (localStorage.getItem(`${pf}_medium_type`) === 'electricity') {
      $('.gas-only').addClass('vol_order hidden')
      $('.electricity-only').removeClass('vol_order hidden')
    }
    else {
      $('.electricity-only').addClass('vol_order hidden')
      $('.gas-only').removeClass('vol_order hidden')
    }

    //if (localStorage.getItem('vol_orders_contract_collective_bill')) {
      //$('.collective_bill_form').removeClass('vol_order hidden')
    //}
    //else {
      //$('.collective_bill_form').addClass('vol_order hidden')
    //}

    if (localStorage.getItem(`${pf}_contract_payment`) === 'direct_debit') {
      $('.direct-debit-only').removeClass('vol_order hidden')
    }
    else {
      $('.direct-debit-only').addClass('vol_order hidden')
    }

    if (localStorage.getItem(`${pf}_contract_payment`) != 'direct_debit') {
      $('.no-direct-debit').removeClass('vol_order hidden')
    }
    else {
      $('.no-direct-debit').addClass('vol_order hidden')
    }


    if (localStorage.getItem(`${pf}_contract_payment`) === 'direct_debit') {
      $('.direct-debit-only').removeClass('vol_order hidden')
    }
    else {
      $('.direct-debit-only').addClass('vol_order hidden')
    }

    // TODO: extend if page was reloading
    if (document.getElementById(name) && name && (
      name.includes(`${pf}_connectiondata_different_billing_address`) ||
      name.includes(`${pf}_connectiondata_old_contract`) ||
      name.includes(`${pf}_connectiondata_remote`) ||
      name.includes(`${pf}_consumptionpoint_meter_type`)
      //name.includes('vol_orders_contract_collective_bill')
    )) {
      if (localStorage.getItem(name) === 'true') {
        document.getElementById(name).classList.remove('hidden')
      }
      else {
        document.getElementById(name).classList.add('hidden')
      }
    }

    if (name && name.includes(`${pf}_consumptionpoint_consumption_type`)) {
      OrderValidation.handleRLMOnly(name)
    }
  }
  static handleSupChangeOnly(key: string) {
    const pf = OrderValidation.getPrefix()
    const currentIndex = Util._getCurrentIndex(key)

    let $parentNode = $(`div[data-pointkey="${currentIndex}"]`)
    if (key.includes(`connectiondata_change`)) {

      let $node = $parentNode.find('.sup-change-only')

      if (localStorage.getItem(key) === 'supplier_change') {
        $node.removeClass('vol_order hidden')
      } else {
        $node.addClass('vol_order hidden')
      }

    } else {

      let $oldContractNode = $parentNode.find('.unterminated-only')

      if (localStorage.getItem(key) === 'terminated' &&
          localStorage.getItem(`${pf}_connectiondata_change[${currentIndex}]`) === 'supplier_change'
      ) {
        $oldContractNode.addClass('vol_order hidden')
      } else {
        $oldContractNode.removeClass('vol_order hidden')
      }

    }




  }

  static handleRLMOnly (name: string) {
    let pf = OrderValidation.getPrefix()

    const type = localStorage.getItem(name)
    const index = Util._getCurrentIndex(name)
    const card = $(`[data-deliverypoint-card-index="${index}"]`);
    const element = $(`[data-name="${pf}_consumptionpoint_specification[${index}]"]`).closest('.rlm-only')

    if (type === 'RLM') {
      element.removeClass('vol_order hidden')
      card.find('.rlm-only').removeClass('vol_order hidden')

      let d = $('.slp-only')
      console.log (d)
      d.addClass('vol_order hidden')

    }
    else { // SLP
      localStorage.setItem(`${pf}_consumptionpoint_specification[${index}]`, '')

      card.find('.rlm-only').addClass('vol_order hidden')

      let d = $('.slp-only')
      d.removeClass('vol_order hidden')

      element.addClass('vol_order hidden')
    }
  }


  static handleConsumptionType (name: string, value: number) {
    let pf = OrderValidation.getPrefix()

    const currentIndex = Util._getCurrentIndex(name)

    if (!/\d+/.test(currentIndex)) {
      return
    }

    const slp = document.getElementById(`slp_[${currentIndex}]`)
    const rlm = document.getElementById(`rlm_[${currentIndex}]`)

    if (slp && rlm) {
      slp.classList.add('d-inline')
      slp.classList.remove('d-none')
      rlm.classList.add('d-inline')
      rlm.classList.remove('d-none')
    }

    const inputName = `${pf}_consumptionpoint_consumption_type[${currentIndex}]`

    if (value >= 100000) {
      document.getElementsByName(inputName).forEach(input => {
        input.checked = input.value === 'RLM'
      })

      localStorage.setItem(inputName, 'RLM')
    }

    if (value < 60000) {
      document.getElementsByName(inputName).forEach(input => {
        input.checked = input.value === 'SLP'
      })

      localStorage.setItem(inputName, 'SLP')
    }

    OrderValidation.loadIndustrySectors(inputName)
  }

  static hideConsumptionType (hideRLM: boolean, index: number) {
    let pf = OrderValidation.getPrefix()

    const inputName = `${pf}_consumptionpoint_consumption_type[${index}]`
    const hideKey = hideRLM ? 'rlm' : 'slp'
    const currentValue = hideRLM ? 'SLP' : 'RLM'

    const nd = document.getElementById(`${hideKey}_[${index}]`)

    if (nd) {
      nd.classList.add('d-none')
      nd.classList.remove('d-inline')
    }

    document.getElementsByName(inputName).forEach(input => {
      input.checked = input.value === currentValue
    })

    localStorage.setItem(inputName, currentValue)

    if (!hideRLM) {
      OrderValidation.loadIndustrySectors(inputName)
    }
  }

  static async loadIndustrySectors (name: string) {
    let pf = OrderValidation.getPrefix()
    // eslint-disable-next-line max-len
    const url = '/bestellprozess?tx_voltego_orders%5Baction%5D=businesstype&tx_voltego_orders%5Bcontroller%5D=Ajax&type=1335898897'

    if (name.includes('consumption_type')) {
      const currentIndex = Util._getCurrentIndex(name)

      await $.ajax({
        type: 'POST',
        url,
        contentType: 'application/x-www-form-urlencoded',
        // eslint-disable-next-line max-len
        data: `business_type_medium_type=${localStorage.getItem(`${pf}_medium_type`)}&business_type_consumption_type=${localStorage.getItem(`${pf}_consumptionpoint_consumption_type[${currentIndex}]`) || 'RLM'}`,
        success: options => {
          Dropdown.rebuild(`${pf}_consumptionpoint_business_type[${currentIndex}]`, options)
          OrderValidation.handleRLMOnly(`${pf}_consumptionpoint_consumption_type[${currentIndex}]`)
        },
        error: (error) => {
          // eslint-disable-next-line no-console
          console.error(error)
        },
      })
    }
    else {
      for (const key of Object.keys(localStorage)) {
        if (key.includes('consumption_type')) {
          await $.ajax({
            type: 'POST',
            url,
            contentType: 'application/x-www-form-urlencoded',
            // eslint-disable-next-line max-len
            data: `business_type_medium_type=${localStorage.getItem(`${pf}_medium_type`)}&business_type_consumption_type=${localStorage.getItem(key)}`,
            success: options => {
              const currentIndex = key.slice(-2, -1)
              const elementId = `${pf}_consumptionpoint_business_type[${currentIndex}]`

              if (document.getElementById(elementId)) {
                Dropdown.rebuild(elementId, options)
                OrderValidation.handleRLMOnly(key)
              }
            },
            error: (error) => {
              // eslint-disable-next-line no-console
              console.error(error)
            },
          })
        }
      }
    }
  }
}
